import { deriveGreeting } from '@lume/shared/worklist';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCapability } from '../../../hooks/use-capability';
import { useDayRitual } from '../../../hooks/use-day-ritual';
import { useAuthStore } from '../../../stores/auth-store';

import {
  readLastRitualOpen,
  shouldAutoOpenToday,
  writeLastRitualOpen,
} from './day-ritual-storage.util';

import type {
  AiResolvedItemWire,
  ClosingRitualStateWire,
  DayRitualAppointmentItemWire,
  DayRitualPendencyItemWire,
  NeedsYouItemWire,
  OpeningRitualStateWire,
} from '../../../lib/api-client';

type RitualKind = 'opening' | 'closing';

/** Natureza visual do glifo do elo (design 01/03 — craft, sem dado novo). */
type GlyphKind = 'promise' | 'cal' | 'ai' | 'esc' | 'tick' | 'carry';

/** Glifo do elo — SVG inline, cor por natureza (mockup 01/03). Decorativo. */
function Glyph({ kind }: { kind: GlyphKind }): JSX.Element {
  const cls = `pv3-glyph pv3-g-${kind}`;
  const svg = (children: JSX.Element): JSX.Element => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
      {children}
    </svg>
  );
  return (
    <span className={cls} aria-hidden="true">
      {kind === 'promise' &&
        svg(
          <>
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v4l3 2" />
          </>,
        )}
      {kind === 'cal' &&
        svg(
          <>
            <rect x="3" y="4" width="18" height="17" rx="2" />
            <path d="M3 9h18M8 2v4M16 2v4" />
          </>,
        )}
      {kind === 'ai' &&
        svg(
          <>
            <circle cx="12" cy="12" r="9" />
            <path d="M9 12l2 2 4-4" />
          </>,
        )}
      {kind === 'esc' &&
        svg(
          <>
            <path d="M12 9v4M12 17h.01" />
            <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
          </>,
        )}
      {kind === 'tick' && svg(<path d="M5 12l5 5L20 6" />)}
      {kind === 'carry' && svg(<path d="M5 12h14M13 6l6 6-6 6" />)}
    </span>
  );
}

/** Elo do rito — «o quê + com quem + gesto» sob a linha dramática do ato. */
function RitualRow({
  glyph,
  title,
  personName,
  detail,
  actionLabel,
  onAction,
}: {
  glyph: GlyphKind;
  title: string;
  personName: string | null;
  detail?: string | undefined;
  actionLabel?: string | undefined;
  onAction?: (() => void) | undefined;
}): JSX.Element {
  const hasMeta = personName !== null || detail !== undefined;
  return (
    <li className="pv3-row">
      <Glyph kind={glyph} />
      <div className="pv3-rb">
        <div className="pv3-rb__t">{title}</div>
        {hasMeta && (
          <div className="pv3-rb__m">
            {personName !== null && (
              <>
                com <b>{personName}</b>
              </>
            )}
            {personName !== null && detail !== undefined && ' · '}
            {detail !== undefined && detail}
          </div>
        )}
      </div>
      {actionLabel !== undefined && onAction !== undefined && (
        <button type="button" className="minibtn" onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </li>
  );
}

/** Seção-ato com hierarquia dramática (spine colorido + count pill por natureza). */
function ActSection({
  variant,
  title,
  count,
  sub,
  delayClass,
  aiDot = false,
  children,
}: {
  variant: 'now' | 'ai' | 'you' | 'proved' | 'carry';
  title: string;
  count: number;
  sub: string;
  delayClass: string;
  aiDot?: boolean;
  children: JSX.Element;
}): JSX.Element {
  return (
    <section className={`pv3-act pv3-act--${variant} pv3-anim ${delayClass}`}>
      <div className="pv3-act__head">
        <span className="pv3-act__spine" />
        {aiDot && <span className="pv3-ai-dot" />}
        <div className="pv3-act__h">
          <h2>{title}</h2>
          <span className="pv3-act__count">{count}</span>
        </div>
        <span className="pv3-act__sub">{sub}</span>
      </div>
      {children}
    </section>
  );
}

function formatDeadline(iso: string | null): string | undefined {
  if (iso === null) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateLabel(nowMs: number): string {
  return new Date(nowMs).toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  });
}

const BLOCK_FAILED = 'Não foi possível carregar este bloco.';

interface RitualHandlers {
  onOpenConversation: (conversationId: string) => void;
  onOpenPendencies: () => void;
  onOpenAgenda: () => void;
  onClose: () => void;
}

interface OpeningContentProps extends RitualHandlers {
  state: OpeningRitualStateWire;
  greeting: string;
  dateLabel: string;
}

function OpeningContent({
  state,
  greeting,
  dateLabel,
  onOpenConversation,
  onOpenPendencies,
  onOpenAgenda,
  onClose,
}: OpeningContentProps): JSX.Element {
  const due = state.dueToday;
  const dueItems: (DayRitualPendencyItemWire | DayRitualAppointmentItemWire)[] =
    due !== null ? [...due.commitments, ...due.others, ...due.appointments] : [];
  const aiResolvedItems: AiResolvedItemWire[] = state.aiResolved?.items ?? [];
  const needsYouItems: NeedsYouItemWire[] = state.needsYou?.items ?? [];

  const compromissos = due !== null ? due.commitments.length + due.others.length : 0;
  const agendamentos = due !== null ? due.appointments.length : 0;
  const aiFechou = state.aiResolved?.count ?? 0;
  const hasWeight = compromissos + agendamentos + aiFechou > 0;

  // Medidor de carga do dia (Inc 2) — composição REAL da carga comprometida,
  // dividida por natureza. SEM teto de capacidade (não existe sinal real →
  // «NUNCA inventar»): é barra de composição, não fill-vs-teto. Os segmentos
  // somam exatamente `dueToday.count` (mesmas arrays já capadas na fonte).
  const nowMs = Date.now();
  let overdue = 0;
  let dueLater = 0;
  if (due !== null) {
    for (const p of [...due.commitments, ...due.others]) {
      const t = p.deadline !== null ? new Date(p.deadline).getTime() : Number.NaN;
      if (!Number.isNaN(t) && t < nowMs) overdue += 1;
      else dueLater += 1;
    }
  }
  const planned = agendamentos;
  const loadTotal = overdue + dueLater + planned;
  const showLoad = due !== null && loadTotal > 0;

  return (
    <>
      <header className="pv3-ritual-head pv3-anim pv3-d1">
        <div>
          <p className="pv3-ritual-eyebrow">Abertura do dia · {dateLabel}</p>
          <h1 className="pv3-ritual-title">{greeting}.</h1>
        </div>
        <button type="button" className="pv3-ritual-ghost" onClick={onClose}>
          Ver a Corrente
        </button>
      </header>

      <p className="pv3-ritual-stake pv3-anim pv3-d2">
        {hasWeight ? 'Hoje a corrente pesa. ' : 'Hoje a corrente está leve. '}
        {due !== null && (
          <>
            <b>
              {compromissos} compromisso{compromissos === 1 ? '' : 's'}
            </b>{' '}
            {compromissos === 1 ? 'vence' : 'vencem'} hoje e há{' '}
            <b>
              {agendamentos} agendamento{agendamentos === 1 ? '' : 's'}
            </b>
            .{' '}
          </>
        )}
        {state.aiResolved !== null && (
          <>
            A IA fechou{' '}
            <b className="ai">
              {aiFechou} conversa{aiFechou === 1 ? '' : 's'}
            </b>{' '}
            enquanto você esteve fora.
          </>
        )}
      </p>

      {showLoad && (
        <div className="pv3-cap pv3-anim pv3-d2">
          <div className="pv3-cap__top">
            <span className="pv3-cap__ttl">A carga de hoje</span>
            <span className="pv3-cap__meta">
              {loadTotal} toque{loadTotal === 1 ? '' : 's'} comprometido{loadTotal === 1 ? '' : 's'}
            </span>
          </div>
          <div className="pv3-cap__bar">
            {overdue > 0 && (
              <div className="pv3-cap__seg pv3-cap__seg--now" style={{ flexGrow: overdue }} />
            )}
            {dueLater > 0 && (
              <div className="pv3-cap__seg pv3-cap__seg--soon" style={{ flexGrow: dueLater }} />
            )}
            {planned > 0 && (
              <div className="pv3-cap__seg pv3-cap__seg--plan" style={{ flexGrow: planned }} />
            )}
          </div>
          <div className="pv3-cap__legend">
            {overdue > 0 && (
              <span>
                <i style={{ background: 'var(--danger-fg)' }} />
                vencido ({overdue})
              </span>
            )}
            {dueLater > 0 && (
              <span>
                <i style={{ background: 'var(--warning-fg)' }} />
                vence hoje ({dueLater})
              </span>
            )}
            {planned > 0 && (
              <span>
                <i style={{ background: 'var(--brand)' }} />
                agendado ({planned})
              </span>
            )}
          </div>
        </div>
      )}

      <div className="pv3-acts">
        <ActSection
          variant="now"
          title="Vence hoje"
          count={due?.count ?? 0}
          sub="o que o dia deve"
          delayClass="pv3-d3"
        >
          {due === null ? (
            <p className="pv3-act__empty">{BLOCK_FAILED}</p>
          ) : dueItems.length === 0 ? (
            <p className="pv3-act__empty">Nada vence hoje.</p>
          ) : (
            <ul className="pv3-act__rows">
              {dueItems.map((item) =>
                'scheduledAt' in item ? (
                  <RitualRow
                    key={`appt-${item.id}`}
                    glyph="cal"
                    title={item.title}
                    personName={item.personName}
                    detail={formatDeadline(item.scheduledAt)}
                    actionLabel="Abrir"
                    onAction={onOpenAgenda}
                  />
                ) : (
                  <RitualRow
                    key={`pend-${item.pendencyId}`}
                    glyph="promise"
                    title={item.title}
                    personName={item.personName}
                    detail={formatDeadline(item.deadline)}
                    actionLabel="Abrir"
                    onAction={() =>
                      item.conversationId !== null
                        ? onOpenConversation(item.conversationId)
                        : onOpenPendencies()
                    }
                  />
                ),
              )}
            </ul>
          )}
        </ActSection>

        <ActSection
          variant="ai"
          title="A IA resolveu — com prova"
          count={state.aiResolved?.count ?? 0}
          sub="desde o seu último pouso"
          delayClass="pv3-d4"
          aiDot
        >
          {state.aiResolved === null ? (
            <p className="pv3-act__empty">{BLOCK_FAILED}</p>
          ) : aiResolvedItems.length === 0 ? (
            <p className="pv3-act__empty">A IA não fechou nenhuma conversa desde o último pouso.</p>
          ) : (
            <ul className="pv3-act__rows">
              {aiResolvedItems.map((item) => (
                <RitualRow
                  key={item.conversationId}
                  glyph="ai"
                  title={item.summary}
                  personName={item.personName}
                  actionLabel="Auditar"
                  onAction={() => onOpenConversation(item.conversationId)}
                />
              ))}
            </ul>
          )}
        </ActSection>

        <ActSection
          variant="you"
          title="Só você decide"
          count={state.needsYou?.count ?? 0}
          sub="exige julgamento humano"
          delayClass="pv3-d5"
        >
          {state.needsYou === null ? (
            <p className="pv3-act__empty">{BLOCK_FAILED}</p>
          ) : needsYouItems.length === 0 ? (
            <p className="pv3-act__empty">Nada exige sua decisão agora.</p>
          ) : (
            <ul className="pv3-act__rows">
              {needsYouItems.map((item, idx) => (
                <RitualRow
                  key={`${item.kind}-${item.conversationId}-${String(idx)}`}
                  glyph={item.kind === 'escalation' ? 'esc' : 'ai'}
                  title={
                    item.kind === 'escalation' ? 'Escalação aguardando' : 'Proposta de transição'
                  }
                  personName={item.personName}
                  detail={item.reason ?? undefined}
                  actionLabel={item.kind === 'escalation' ? 'Decidir' : 'Revisar'}
                  onAction={() => onOpenConversation(item.conversationId)}
                />
              ))}
            </ul>
          )}
        </ActSection>
      </div>
    </>
  );
}

interface ClosingContentProps extends RitualHandlers {
  state: ClosingRitualStateWire;
  dateLabel: string;
}

function ClosingContent({
  state,
  dateLabel,
  onOpenConversation,
  onOpenPendencies,
  onOpenAgenda,
  onClose,
}: ClosingContentProps): JSX.Element {
  const proved = state.provedToday;
  const provedItems: DayRitualPendencyItemWire[] =
    proved !== null ? [...proved.commitments, ...proved.others] : [];
  const carry = state.carryOver;
  const carryItems: (DayRitualPendencyItemWire | DayRitualAppointmentItemWire)[] =
    carry !== null ? [...carry.commitments, ...carry.others, ...carry.appointments] : [];

  const cumpridos = proved?.count ?? 0;
  const encerradas = proved?.conversationsClosedCount ?? 0;

  // Métrica-herói «promessa cumprida no prazo» (Inc 3) — dado REAL do audit
  // nativo (resolvedAt <= deadline). `total === 0` (nenhuma promessa resolvida
  // hoje) ⇒ esconde: um «%» sem base mentiria (anti-anestesia). `?? null`
  // normaliza undefined→null (skew-proof, consistente com os blocos irmãos).
  const metric = state.promiseMetric ?? null;
  const metricPct =
    metric !== null && metric.total > 0 ? Math.round((metric.onTime / metric.total) * 100) : null;

  return (
    <>
      <header className="pv3-ritual-head pv3-ritual-head--closing pv3-anim pv3-d1">
        <div>
          <p className="pv3-ritual-eyebrow">Fechamento do dia · {dateLabel}</p>
          <h1 className="pv3-ritual-title">O dia, no espelho.</h1>
        </div>
        <button type="button" className="pv3-ritual-ghost" onClick={onClose}>
          Voltar à Corrente
        </button>
      </header>

      <p className="pv3-ritual-stake pv3-anim pv3-d2">
        {proved !== null ? (
          <>
            Você cumpriu{' '}
            <b className="ok">
              {cumpridos} compromisso{cumpridos === 1 ? '' : 's'}
            </b>{' '}
            e encerrou{' '}
            <b>
              {encerradas} conversa{encerradas === 1 ? '' : 's'}
            </b>{' '}
            hoje. O que ficou aberto não sumiu — rola com idade honesta para amanhã.
          </>
        ) : (
          'O espelho do dia — dado real, auditável.'
        )}
      </p>

      {metric !== null && metricPct !== null && (
        <div className="pv3-metric pv3-anim pv3-d2">
          <span className="pv3-metric__big">{metricPct}%</span>
          <span className="pv3-metric__lbl">
            <b>Promessa cumprida no prazo</b>
            <br />
            cumpridas dentro do prazo que você prometeu
          </span>
          <div className="pv3-metric__bar">
            <div className="pv3-metric__track">
              <div className="pv3-metric__fill" style={{ width: `${metricPct}%` }} />
            </div>
            <div className="pv3-metric__cap">
              {metric.onTime} de {metric.total} promessa{metric.total === 1 ? '' : 's'} · hoje
            </div>
          </div>
        </div>
      )}

      {/* Dívida acumulada — vencidas em aberto de QUALQUER dia. Linha separada de
          propósito: não contamina o aproveitamento do dia, que segue fechável.
          Some quando zerada (honest-defer, molde da própria métrica acima). */}
      {metric !== null && metric.openBreached > 0 && (
        <p className="pv3-metric__debt pv3-anim pv3-d2">
          {metric.openBreached} promessa{metric.openBreached === 1 ? '' : 's'} quebrada
          {metric.openBreached === 1 ? '' : 's'} em aberto
        </p>
      )}

      <div className="pv3-acts">
        <ActSection
          variant="proved"
          title="O que o dia provou"
          count={proved?.count ?? 0}
          sub="dado real · auditável"
          delayClass="pv3-d3"
        >
          {proved === null ? (
            <p className="pv3-act__empty">{BLOCK_FAILED}</p>
          ) : provedItems.length === 0 ? (
            <p className="pv3-act__empty">Nenhum compromisso ou pendência resolvido hoje.</p>
          ) : (
            <ul className="pv3-act__rows">
              {provedItems.map((item) => (
                <RitualRow
                  key={item.pendencyId}
                  glyph="tick"
                  title={item.title}
                  personName={item.personName}
                  actionLabel="Abrir"
                  onAction={() =>
                    item.conversationId !== null
                      ? onOpenConversation(item.conversationId)
                      : onOpenPendencies()
                  }
                />
              ))}
            </ul>
          )}
        </ActSection>

        <ActSection
          variant="carry"
          title="Rola para amanhã"
          count={carry?.count ?? 0}
          sub="nunca some — carry-over honesto"
          delayClass="pv3-d4"
        >
          {carry === null ? (
            <p className="pv3-act__empty">{BLOCK_FAILED}</p>
          ) : carryItems.length === 0 ? (
            <p className="pv3-act__empty">Nada fica para amanhã — tudo em dia.</p>
          ) : (
            <ul className="pv3-act__rows">
              {carryItems.map((item) =>
                'scheduledAt' in item ? (
                  <RitualRow
                    key={`appt-${item.id}`}
                    glyph="cal"
                    title={item.title}
                    personName={item.personName}
                    detail={formatDeadline(item.scheduledAt)}
                    actionLabel="Abrir"
                    onAction={onOpenAgenda}
                  />
                ) : (
                  <RitualRow
                    key={`pend-${item.pendencyId}`}
                    glyph="carry"
                    title={item.title}
                    personName={item.personName}
                    detail={formatDeadline(item.deadline)}
                    actionLabel="Abrir"
                    onAction={() =>
                      item.conversationId !== null
                        ? onOpenConversation(item.conversationId)
                        : onOpenPendencies()
                    }
                  />
                ),
              )}
            </ul>
          )}
        </ActSection>

        <ActSection
          variant="ai"
          title="A IA cobre a noite"
          count={state.aiCovering?.count ?? 0}
          sub="enquanto você descansa"
          delayClass="pv3-d5"
        >
          {state.aiCovering === null ? (
            <p className="pv3-act__empty">{BLOCK_FAILED}</p>
          ) : state.aiCovering.count === 0 ? (
            <p className="pv3-act__empty">A IA não está conduzindo nenhuma conversa agora.</p>
          ) : (
            <div className="pv3-ai-cover">
              <span className="pv3-ai-dot" />
              <span>
                A IA segue conduzindo <b>{state.aiCovering.count} conversa(s)</b> de baixo risco em
                H_c. Se algo exigir julgamento, ela segura e deixa na sua Abertura de amanhã — nunca
                fala por cima de você.
              </span>
            </div>
          )}
        </ActSection>
      </div>
    </>
  );
}

/**
 * DayRitualOverlay — S3 Abertura e Fechamento do dia (Corrente de Compromissos,
 * P2 da tese, absorve o Item 371).
 *
 * Inc 1 «wow»: o rito deixa de ser o `ModalBase` genérico (640px) e passa a ser
 * um STAGE imersivo — scrim fixo sobre a Corrente REAL (viva atrás), atos com
 * hierarquia dramática (spine + count pill por natureza) e cascata de entrada.
 * CSS-only, ZERO backend novo: consome o MESMO `day-ritual` e a MESMA cap.
 *
 * Self-contained: cap `operator-daily-ritual` gateia o FETCH (`useDayRitual`
 * `enabled`) e o RENDER (retorna `null` sem a cap — nada muda no painel).
 * Auto-abre a Abertura 1×/dia (localStorage `lume.dayRitual.lastOpen.<userId>`,
 * design §5); reabrível pelos triggers do header; o Fechamento é acionado
 * manualmente (v1 SEM gatilho automático — design §3).
 */
export function DayRitualOverlay(): JSX.Element | null {
  const hasCap = useCapability('operator-daily-ritual');
  const userId = useAuthStore((s) => s.user?.id ?? '');
  const fullName = useAuthStore((s) => s.user?.name ?? '');
  const navigate = useNavigate();
  const [kind, setKind] = useState<RitualKind | null>(null);
  const [since, setSince] = useState<string | undefined>(undefined);
  const autoOpenAttempted = useRef(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasCap || userId === '' || autoOpenAttempted.current) return;
    autoOpenAttempted.current = true;
    const lastOpen = readLastRitualOpen(userId);
    if (shouldAutoOpenToday(lastOpen, Date.now())) {
      setSince(lastOpen ?? undefined);
      setKind('opening');
    }
  }, [hasCap, userId]);

  const enabled = hasCap && kind !== null;
  const ritual = useDayRitual(kind ?? 'opening', enabled, kind === 'opening' ? since : undefined);

  // Foco no diálogo ao abrir → Esc funciona (melhoria de a11y vs ModalBase).
  useEffect(() => {
    if (kind !== null) dialogRef.current?.focus();
  }, [kind]);

  if (!hasCap) return null;

  function openOpening(): void {
    setSince(readLastRitualOpen(userId) ?? undefined);
    setKind('opening');
  }

  function openClosing(): void {
    setKind('closing');
  }

  function handleClose(): void {
    if (kind === 'opening' && userId !== '') {
      writeLastRitualOpen(userId, new Date().toISOString());
    }
    setKind(null);
  }

  function openConversation(conversationId: string): void {
    handleClose();
    navigate(`/atendimento/conversation/${conversationId}`);
  }

  function openPendencies(): void {
    handleClose();
    navigate('/pendencias');
  }

  function openAgenda(): void {
    handleClose();
    navigate('/agenda');
  }

  const now = Date.now();
  const firstName = fullName.split(' ')[0] ?? '';
  const greeting = deriveGreeting(now, firstName);
  const dateLabel = formatDateLabel(now);
  const title = kind === 'closing' ? 'Fechamento do dia' : 'Abertura do dia';

  return (
    <>
      <div className="pv3__ritual-triggers">
        <button type="button" className="pv3-ritual-btn" onClick={openOpening}>
          Abertura do dia
        </button>
        <button type="button" className="pv3-ritual-btn" onClick={openClosing}>
          Fechar o dia
        </button>
      </div>

      {kind !== null && (
        <div
          className="pv3-ritual-stage"
          role="presentation"
          onClick={(e) => {
            // clique no fundo (gutters do stage) fecha; clique no diálogo não propaga
            if (e.target === e.currentTarget) handleClose();
          }}
          onKeyDown={(e) => {
            // Esc fecha (borbulha do diálogo focado) — paridade com ModalBase
            if (e.key === 'Escape') handleClose();
          }}
        >
          <div
            className="pv3-ritual"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            ref={dialogRef}
            tabIndex={-1}
          >
            {/* controle de fechar sempre alcançável nos estados loading/erro
                (o header ghost + CTA só existem com dado) — fecha o furo de
                a11y em mobile onde não há Esc nem gutter clicável */}
            {ritual.data === undefined && (
              <button
                type="button"
                className="pv3-ritual-close"
                onClick={handleClose}
                aria-label="Fechar"
              >
                ×
              </button>
            )}
            <div className="pv3-ritual-card">
              {ritual.isLoading && <p className="pv3-ritual-loading">Carregando…</p>}
              {ritual.isError && (
                <p className="pv3-ritual-loading">
                  Não foi possível carregar o rito. Tente novamente.
                </p>
              )}
              {ritual.data?.kind === 'opening' && (
                <OpeningContent
                  state={ritual.data}
                  greeting={greeting}
                  dateLabel={dateLabel}
                  onOpenConversation={openConversation}
                  onOpenPendencies={openPendencies}
                  onOpenAgenda={openAgenda}
                  onClose={handleClose}
                />
              )}
              {ritual.data?.kind === 'closing' && (
                <ClosingContent
                  state={ritual.data}
                  dateLabel={dateLabel}
                  onOpenConversation={openConversation}
                  onOpenPendencies={openPendencies}
                  onOpenAgenda={openAgenda}
                  onClose={handleClose}
                />
              )}

              {ritual.data !== undefined && (
                <div className="pv3-ritual-foot pv3-anim pv3-d6">
                  <p className="pv3-ritual-note">
                    {kind === 'opening'
                      ? 'Reabrível a qualquer momento pelo header «Abertura do dia». A Corrente fica atrás, viva.'
                      : 'Sem gatilho automático de fim de turno — você fecha o dia quando ele acaba. O carry-over reaparece amanhã, com a idade contando.'}
                  </p>
                  <button type="button" className="pv3-ritual-cta" onClick={handleClose}>
                    {kind === 'opening' ? 'Começar o dia' : 'Encerrar'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
